export class ChessHttpClient {
    constructor(baseUrl) {
        Object.defineProperty(this, "baseUrl", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: baseUrl
        });
    }
    getAuthHeader(token) {
        const headers = { 'Content-Type': 'application/json' };
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        return headers;
    }
    async createRoom() {
        const res = await fetch(`${this.baseUrl}/rooms`, { method: 'POST' });
        const data = await this.parseJson(res);
        if (!res.ok) {
            throw new Error(`Failed to create room: ${res.status}`);
        }
        if (typeof data === 'object' && data !== null && 'ok' in data) {
            return data;
        }
        throw new Error('Invalid create room response format');
    }
    async joinRoom(code) {
        const res = await fetch(`${this.baseUrl}/rooms/${code}/join`, {
            method: 'POST',
        });
        const data = await this.parseJson(res);
        if (!res.ok) {
            if (res.status === 404)
                throw new Error('Room not found');
            if (res.status === 409)
                throw new Error('Room already has two players');
            throw new Error(`Failed to join room: ${res.status}`);
        }
        if (typeof data === 'object' && data !== null && 'ok' in data) {
            return data;
        }
        throw new Error('Invalid join room response format');
    }
    async parseJson(res) {
        try {
            return await res.json();
        }
        catch (error) {
            throw new Error(`Invalid JSON response from server: ${res.status}`);
        }
    }
    async getGameState(gameId, token) {
        const res = await fetch(`${this.baseUrl}/games/${gameId}`, {
            headers: this.getAuthHeader(token),
        });
        if (!res.ok) {
            if (res.status === 410)
                throw new Error('Token expired');
            throw new Error(`Failed to fetch game: ${res.status}`);
        }
        return res.json();
    }
    async getLegalMoves(gameId, from, token) {
        const res = await fetch(`${this.baseUrl}/games/${gameId}/legal-moves?from=${from}`, { headers: this.getAuthHeader(token) });
        if (!res.ok)
            throw new Error(`Failed to fetch legal moves: ${res.status}`);
        return res.json();
    }
    async executeMove(gameId, from, to, token, promotion) {
        const res = await fetch(`${this.baseUrl}/games/${gameId}/move`, {
            method: 'POST',
            headers: this.getAuthHeader(token),
            body: JSON.stringify({ from, to, ...(promotion && { promotion }) }),
        });
        let data;
        try {
            data = await res.json();
        }
        catch (error) {
            throw new Error(`Invalid JSON response from server: ${res.status}`);
        }
        if (!res.ok) {
            if (typeof data === 'object' && data !== null && 'error' in data) {
                return data;
            }
            throw new Error(`Failed to execute move: ${res.status}`);
        }
        if (typeof data === 'object' && data !== null && 'ok' in data) {
            return data;
        }
        throw new Error('Invalid move response format');
    }
    async pollForMoves(gameId, moveNumber, token) {
        const res = await fetch(`${this.baseUrl}/games/${gameId}/since/${moveNumber}`, {
            headers: this.getAuthHeader(token),
        });
        if (!res.ok) {
            if (res.status === 410)
                throw new Error('Token expired');
            throw new Error(`Failed to poll: ${res.status}`);
        }
        return res.json();
    }
    async resignGame(gameId, token) {
        const res = await fetch(`${this.baseUrl}/games/${gameId}/resign`, {
            method: 'POST',
            headers: this.getAuthHeader(token),
            // Fastify's JSON body parser rejects a Content-Type: application/json request with no
            // body, so send an explicit empty object even though this endpoint takes no payload.
            body: '{}',
        });
        let data;
        try {
            data = await res.json();
        }
        catch (error) {
            throw new Error(`Invalid JSON response from server: ${res.status}`);
        }
        if (!res.ok) {
            if (typeof data === 'object' && data !== null && 'error' in data) {
                return data;
            }
            throw new Error(`Failed to resign: ${res.status}`);
        }
        if (typeof data === 'object' && data !== null && 'ok' in data) {
            return data;
        }
        throw new Error('Invalid resign response format');
    }
    async offerDraw(gameId, token) {
        const res = await fetch(`${this.baseUrl}/games/${gameId}/offer-draw`, {
            method: 'POST',
            headers: this.getAuthHeader(token),
            body: '{}',
        });
        return this.parseDrawOfferResponse(res, 'Failed to offer draw');
    }
    async acceptDraw(gameId, token) {
        const res = await fetch(`${this.baseUrl}/games/${gameId}/accept-draw`, {
            method: 'POST',
            headers: this.getAuthHeader(token),
            body: '{}',
        });
        return this.parseDrawOfferResponse(res, 'Failed to accept draw');
    }
    async rejectDraw(gameId, token) {
        const res = await fetch(`${this.baseUrl}/games/${gameId}/reject-draw`, {
            method: 'POST',
            headers: this.getAuthHeader(token),
            body: '{}',
        });
        return this.parseDrawOfferResponse(res, 'Failed to reject draw');
    }
    async parseDrawOfferResponse(res, failureMessage) {
        let data;
        try {
            data = await res.json();
        }
        catch (error) {
            throw new Error(`Invalid JSON response from server: ${res.status}`);
        }
        if (!res.ok) {
            if (typeof data === 'object' && data !== null && 'error' in data) {
                return data;
            }
            throw new Error(`${failureMessage}: ${res.status}`);
        }
        if (typeof data === 'object' && data !== null && 'ok' in data) {
            return data;
        }
        throw new Error(`Invalid response format from ${failureMessage.toLowerCase()}`);
    }
}
//# sourceMappingURL=http-client.js.map